import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import { currentUser, duckify } from 'utils/duckify';

const PaymentProfileOptions = ({
  billingConfig,
  customer,
  showAutopayOption,
  showSaveProfileOption,
  requireSaveProfile,
  savePaymentProfile,
  paymentMethod,
  autopay,
  onChange = () => false,
  disabled,
  currentUser
}) => {
  const { require_autopay: requireAutopay, autopay_default: defaultAutopay} = billingConfig;
  const { billing_enabled: billingEnabled } = customer;

  const disableAutopay = !showAutopayOption || billingConfig.disable_autopay;

  const supportsAutopay = (customer.deposit_payment_methods || []).includes(paymentMethod);

  const impersonate = currentUser && currentUser.impersonate;

  useEffect(() => {
    // Both options should be set to false and hidden
    if (impersonate) {
      onChange({
        savePaymentProfile: false,
        autopay: false
      });
    // If we are not showing autopay as an option, we should not be updating the autopay on the payment profile one way or another
    } else if (!showAutopayOption) {
      onChange({
        savePaymentProfile,
        autopay: null
      })
    } else if (!defaultAutopay && !!autopay) {
      onChange({
        savePaymentProfile,
        autopay: false
      });
    }}, [])

  // If we're looking at a payment method that doesn't support autopay,
  // need to call back to parent component and let it know
  useEffect(() => {
    if(autopay && !supportsAutopay && showAutopayOption) {
      onChange({
        savePaymentProfile,
        autopay: false
      });
    }
  }, [paymentMethod, autopay]);


  const togglePaymentProfile = () => {
    onChange({
      savePaymentProfile: !savePaymentProfile,
      autopay: !savePaymentProfile && autopay
    });
  };

  const toggleAutopay = () => {
    onChange({
      savePaymentProfile,
      autopay: savePaymentProfile && !autopay
    });
  };

  return <div id={'paymentProfileComponent'}>
    {showSaveProfileOption && !impersonate && (
      <div
        className='save-payment-profile'
      >
        <div className='text-end save-payment-profile-checkbox'>
          <input
            type='checkbox'
            onChange={togglePaymentProfile}
            checked={!!savePaymentProfile || requireSaveProfile || requireAutopay}
            disabled={requireAutopay || requireSaveProfile || disabled}
          />
        </div>

        <div className={'save-payment-profile-info'}>
          <div className={'save-payment-profile-header'}>
            Save my information for future payments.
          </div>
          <div className={'save-payment-profile-description'}>
            By saving your information, you agree to {customer.name}'s
            {' '}<a target={'_blank'} href={'/web_snippets/autopay_terms'}>
            terms &amp; conditions.</a>
          </div>
          <div className={'save-payment-profile-description'}>
            All information is securely transmitted and stored via 256-bit encryption.
          </div>
        </div>
      </div>
    )}


    { /* Ensure that the autopay option is shown only if this is an allowed payment method for billing */ }
    {billingEnabled && !disableAutopay && supportsAutopay && !impersonate && (
    <div className={`save-payment-profile autopay ${showSaveProfileOption && !savePaymentProfile ? 'disabled' : ''}`}>
      <div className='autopay-checkbox'>
        <input
          type={'checkbox'}
          name={'payment_profile_autopay'}
          onChange={toggleAutopay}
          checked={!!autopay && (supportsAutopay || requireAutopay)}
          disabled={(autopay && requireAutopay) || (showSaveProfileOption && !savePaymentProfile) || disabled}
        />
      </div>

      <div className={'save-payment-profile-info'}>
        <div className={'save-payment-profile-header'}>
          Enroll in AutoPay for future tuition payments.
        </div>
        <div className={'save-payment-profile-description'}>
          By enrolling in AutoPay, you agree to {customer.name}'s
          {' '}<a target={'_blank'} href={'/web_snippets/autopay_terms'}>
          terms &amp; conditions
        </a>.
        </div>
        <div className={'save-payment-profile-description'}>
          You can cancel or update your billing information at any time.
        </div>
        <div className={'save-payment-profile-description'}>
          AutoPay can take up to 3 days to process regardless of your billing date.
        </div>
      </div>
    </div>
    )}

  </div>;
};

PaymentProfileOptions.propTypes = {
  autopay: PropTypes.bool,
  billingConfig: PropTypes.shape({
    disable_autopay: PropTypes.bool,
    require_autopay: PropTypes.bool,
    autopay_default: PropTypes.bool
  }),
  customer: PropTypes.shape({
    billing_enabled: PropTypes.bool,
    deposit_payment_methods: PropTypes.arrayOf(PropTypes.string),
    name: PropTypes.string
  }),
  onChange: PropTypes.func,
  savePaymentProfile: PropTypes.bool,
  paymentMethod: PropTypes.string,
  showSaveProfileOption: PropTypes.bool,
  requireSaveProfile: PropTypes.bool,
  disabled: PropTypes.bool
};

PaymentProfileOptions.defaultProps = {
  billingConfig: {},
  customer: {},
  onChange: () => false,
  paymentMethod: '',
  showSaveProfileOption: true,
  savePaymentProfile: true,
  requireSaveProfile: false,
  disabled: false
};

const mapDucksToProps = () => ({ currentUser });
export default duckify(mapDucksToProps)(PaymentProfileOptions);
